import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchData } from '../../helpers/apiUtils';
import {
  AffiliateAusspielErgebnis,
  AffiliateLinksZustand,
  AffiliateOffeneSeite,
  AffiliatePruefBefund,
  ROLLOUT_TEXTE,
} from './types';

interface Props {
  brokerId: number | null;
  brokerName: string;
  onSchliessen: () => void;
  /** Nach Speichern/Ausspielen — Matrix und ggf. Editor laden neu. */
  onGeaendert: () => void;
}

const BEFUND_FARBE: Record<AffiliatePruefBefund['art'], string> = {
  fehler: 'error',
  warnung: 'warning.main',
  info: 'text.secondary',
};
const BEFUND_PRAEFIX: Record<AffiliatePruefBefund['art'], string> = {
  fehler: 'Fehler: ',
  warnung: 'Warnung: ',
  info: 'Hinweis (besteht schon): ',
};

/**
 * „Affiliate-Links" eines Brokers — alle Portale an EINER Stelle, ein Knopf zum Ausspielen.
 *
 * ============================================================================================
 * WARUM ES DIESEN DIALOG GIBT
 * ============================================================================================
 * Christian hat am 2026-10-20 sechs neue Freedom24-Links in sechs Profilen eingetragen und nur
 * eines ausgespielt; die FT-Seite zeigte den neuen Link trotzdem nicht. Ein Link-Wechsel betrifft
 * Profilseite, Übersichtskarte UND die Vergleichsseiten desselben Portals — der Editor kannte nur
 * die erste. Dieser Dialog zeigt alle drei und spielt sie zusammen aus.
 *
 * ⚠ Was „offen" ist, bestimmt der SERVER bei jedem Laden neu (affiliateLinksService.ts). Nach einem
 * Abbruch zeigt das erneute Öffnen dieselben offenen Seiten — die Liste hier ist eine Anzeige,
 * keine Buchführung.
 *
 * ⚠ Seiten mit FREMDEN, noch nicht ausgespielten Änderungen sind standardmäßig NICHT angehakt
 * (Entscheidung Till 2026-10-23): eine halbfertige Änderung eines anderen live zu stellen ist
 * teurer als ein paar Stunden alter Link — und die Seite bleibt als offen markiert.
 */
const AffiliateLinksDialog: React.FC<Props> = ({
  brokerId,
  brokerName,
  onSchliessen,
  onGeaendert,
}) => {
  const [zustand, setZustand] = useState<AffiliateLinksZustand | null>(null);
  const [eingaben, setEingaben] = useState<Record<string, string>>({});
  const [befunde, setBefunde] = useState<AffiliatePruefBefund[] | null>(null);
  const [bestaetigungNoetig, setBestaetigungNoetig] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set());
  const [ergebnisse, setErgebnisse] = useState<AffiliateAusspielErgebnis[] | null>(null);
  const [meldung, setMeldung] = useState<{
    ton: 'success' | 'warning' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState<null | 'laden' | 'pruefen' | 'speichern' | 'ausspielen'>(null);
  // Laufende Ladevorgänge eines früher geöffneten Brokers dürfen den Zustand nicht überschreiben.
  const ladeFuer = useRef<number | null>(null);

  const uebernehme = useCallback((z: AffiliateLinksZustand, eingabenZuruecksetzen: boolean) => {
    setZustand(z);
    if (eingabenZuruecksetzen) {
      setEingaben(Object.fromEntries(z.portale.map((p) => [p.siteKey, p.affiliateUrl ?? ''])));
    }
    // Vorgabe: angehakt, ausser die Seite trägt fremde ungespielte Änderungen (Q1).
    setGewaehlt(new Set(z.offeneSeiten.filter((s) => !s.fremdeAenderung).map((s) => s.profileId)));
  }, []);

  const laden = useCallback(async () => {
    if (brokerId === null) return;
    ladeFuer.current = brokerId;
    setZustand(null);
    setBefunde(null);
    setBestaetigungNoetig(false);
    setErgebnisse(null);
    setMeldung(null);
    setBusy('laden');
    const r = await fetchData<AffiliateLinksZustand>(
      `/api/broker-hub/brokers/${brokerId}/affiliate-links`,
    );
    if (ladeFuer.current !== brokerId) return;
    setBusy(null);
    if (r.success && r.data) uebernehme(r.data, true);
    else
      setMeldung({ ton: 'error', text: r.error || 'Die Affiliate-Links ließen sich nicht laden.' });
  }, [brokerId, uebernehme]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Läuft für diesen Broker gerade ein Ausspielen aus einem ANDEREN Tab oder von einem Kollegen,
  // meldet der Server `laeuft: true`. Ohne Nachfragen bliebe der Dialog dann gesperrt, bis jemand
  // die Seite neu lädt (Codex-Gegenlesung 2026-10-25) — also alle 5 s nachsehen, bis er frei ist.
  // Die Eingaben bleiben dabei stehen.
  const fremderLauf = busy === null && zustand?.laeuft === true;
  useEffect(() => {
    if (!fremderLauf || brokerId === null) return;
    const fuer = brokerId;
    const t = setInterval(async () => {
      const r = await fetchData<AffiliateLinksZustand>(
        `/api/broker-hub/brokers/${fuer}/affiliate-links`,
      );
      if (ladeFuer.current === fuer && r.success && r.data) uebernehme(r.data, false);
    }, 5000);
    return () => clearInterval(t);
  }, [fremderLauf, brokerId, uebernehme]);

  const geaendert = (zustand?.portale ?? []).filter(
    (p) => p.profileId && (eingaben[p.siteKey] ?? '').trim() !== (p.affiliateUrl ?? ''),
  );
  const befundeFuer = (siteKey: string) => (befunde ?? []).filter((b) => b.siteKey === siteKey);
  const hatFehler = (befunde ?? []).some((b) => b.art === 'fehler');

  const nurGeaenderte = () =>
    Object.fromEntries(geaendert.map((p) => [p.siteKey, (eingaben[p.siteKey] ?? '').trim()]));

  const pruefen = async () => {
    if (brokerId === null) return;
    setBusy('pruefen');
    setMeldung(null);
    const r = await fetchData<{ befunde: AffiliatePruefBefund[]; bestaetigungNoetig: boolean }>(
      `/api/broker-hub/brokers/${brokerId}/affiliate-links/pruefen`,
      { method: 'POST', body: JSON.stringify({ links: nurGeaenderte() }) },
    );
    setBusy(null);
    if (r.success && r.data) {
      setBefunde(r.data.befunde);
      setBestaetigungNoetig(false);
      const f = r.data.befunde.filter((b) => b.art === 'fehler').length;
      const w = r.data.befunde.filter((b) => b.art === 'warnung').length;
      setMeldung(
        f
          ? { ton: 'error', text: `${f} Fehler — so kann nicht gespeichert werden.` }
          : w
            ? {
                ton: 'warning',
                text: `${w} Warnung(en) — bitte ansehen. Speichern fragt danach noch einmal nach.`,
              }
            : { ton: 'success', text: 'Geprüft — keine Auffälligkeiten.' },
      );
    } else setMeldung({ ton: 'error', text: r.error || 'Prüfen fehlgeschlagen.' });
  };

  const speichern = async (bestaetigt: boolean) => {
    if (brokerId === null || !zustand) return;
    setBusy('speichern');
    setMeldung(null);
    const expectedRevisions = Object.fromEntries(
      zustand.portale.filter((p) => p.profileId).map((p) => [String(p.profileId), p.revision]),
    );
    const r = await fetchData<{ geschrieben: string[]; zustand: AffiliateLinksZustand }>(
      `/api/broker-hub/brokers/${brokerId}/affiliate-links`,
      {
        method: 'PUT',
        body: JSON.stringify({ links: nurGeaenderte(), expectedRevisions, bestaetigt }),
      },
    );
    setBusy(null);
    const extra = r as unknown as { befunde?: AffiliatePruefBefund[]; geschrieben?: string[] };
    if (r.success && r.data) {
      uebernehme(r.data.zustand, true);
      setBefunde(null);
      setBestaetigungNoetig(false);
      setMeldung({
        ton: 'success',
        text: `Gespeichert: ${r.data.geschrieben.map((s) => s.toUpperCase()).join(', ')} — noch NICHT live. Unten die offenen Seiten ausspielen.`,
      });
      onGeaendert();
      return;
    }
    if (r.requires_confirmation) {
      setBefunde(extra.befunde ?? []);
      setBestaetigungNoetig(true);
      setMeldung({
        ton: 'warning',
        text: 'Die Prüfung hat Warnungen. Bitte ansehen — dann „Trotzdem speichern".',
      });
      return;
    }
    if (Array.isArray(extra.befunde)) setBefunde(extra.befunde);
    setMeldung({ ton: 'error', text: r.error || 'Speichern fehlgeschlagen.' });
    // Ein Konflikt kann Teile schon geschrieben haben — der Zustand muss neu her, die Eingaben bleiben.
    if (Array.isArray(extra.geschrieben)) {
      const z = await fetchData<AffiliateLinksZustand>(
        `/api/broker-hub/brokers/${brokerId}/affiliate-links`,
      );
      if (z.success && z.data) uebernehme(z.data, false);
      if (extra.geschrieben.length) onGeaendert();
    }
  };

  const ausspielen = async () => {
    if (brokerId === null || !zustand) return;
    const seiten = zustand.offeneSeiten
      .filter((s) => gewaehlt.has(s.profileId))
      .map((s) => ({ profileId: s.profileId, revision: s.revision }));
    if (!seiten.length) return;
    setBusy('ausspielen');
    setMeldung(null);
    setErgebnisse(null);
    const r = await fetchData<{
      ergebnisse: AffiliateAusspielErgebnis[];
      zustand: AffiliateLinksZustand;
    }>(`/api/broker-hub/brokers/${brokerId}/affiliate-links/ausspielen`, {
      method: 'POST',
      body: JSON.stringify({ seiten }),
    });
    setBusy(null);
    if (r.success && r.data) {
      setErgebnisse(r.data.ergebnisse);
      uebernehme(r.data.zustand, false);
      const rot = r.data.ergebnisse.filter((e) => !e.ok).length;
      setMeldung(
        rot
          ? {
              ton: 'warning',
              text: `${r.data.ergebnisse.length - rot} ausgespielt, ${rot} nicht — Gründe unten. Die offenen Seiten bleiben gelistet.`,
            }
          : {
              ton: 'success',
              text: `${r.data.ergebnisse.length} Seite(n) ausgespielt. Sichtbar wird das ggf. erst in bis zu 60 Min (Portal-Cache).`,
            },
      );
      onGeaendert();
    } else {
      // 409 vom Vorlauf: `r.error` nennt bereits jede Seite samt Grund — und es wurde NICHTS
      // ausgespielt. Kein Ausfall, sondern die Liste, die abzuarbeiten ist.
      setMeldung({ ton: 'error', text: r.error || 'Ausspielen fehlgeschlagen.' });
      // Nach einem Lauf, der vielleicht teilweise durchkam (Verbindung weg), den Zustand neu holen.
      const z = await fetchData<AffiliateLinksZustand>(
        `/api/broker-hub/brokers/${brokerId}/affiliate-links`,
      );
      if (z.success && z.data) uebernehme(z.data, false);
    }
  };

  const offene = zustand?.offeneSeiten ?? [];
  const gewaehlteAnzahl = offene.filter((s) => gewaehlt.has(s.profileId)).length;
  // Eigene Anfrage sperrt alles (auch Schließen); ein fremder Lauf nur das Schreiben.
  const lokalBusy = busy !== null;
  const laeuft = lokalBusy || fremderLauf;

  const zeileOffen = (s: AffiliateOffeneSeite) => {
    const erg = ergebnisse?.find((e) => e.profileId === s.profileId);
    return (
      <TableRow key={s.profileId} hover>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={gewaehlt.has(s.profileId)}
            disabled={laeuft}
            onChange={() =>
              setGewaehlt((v) => {
                const n = new Set(v);
                if (n.has(s.profileId)) n.delete(s.profileId);
                else n.add(s.profileId);
                return n;
              })
            }
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {s.pageUrl ? (
              <a href={s.pageUrl} target="_blank" rel="noopener noreferrer">
                {s.titel}
              </a>
            ) : (
              s.titel
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {s.grund}
          </Typography>
          {s.fremdeAenderung && (
            <Typography variant="caption" color="warning.main" display="block">
              Trägt zusätzlich ungespielte Änderungen von {s.fremdeAenderung.wer} (
              {s.fremdeAenderung.wann}
              {s.fremdeAenderung.notiz ? `, „${s.fremdeAenderung.notiz}"` : ''}) — die gehen mit
              raus.
            </Typography>
          )}
          {s.zentraleAenderungenAndererBroker && (
            <Typography variant="caption" color="text.secondary" display="block">
              Nimmt auch die aktuellen Werte der anderen gezeigten Broker mit.
            </Typography>
          )}
          {erg && (
            <Typography variant="caption" color={erg.ok ? 'success.main' : 'error'} display="block">
              {erg.ok
                ? erg.kartePlatziert === false
                  ? 'Ausgespielt — aber die Übersichtskarte wurde NICHT übernommen.'
                  : 'Ausgespielt.'
                : `Nicht ausgespielt: ${erg.error ?? erg.uebersprungen ?? 'unbekannt'}`}
            </Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog
      open={brokerId !== null}
      onClose={lokalBusy ? undefined : onSchliessen}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Affiliate-Links: {brokerName}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Ein Link je Portal. <strong>Speichern</strong> schreibt die Links,{' '}
          <strong>Ausspielen</strong> bringt sie auf die Profilseite, die Übersichtskarte und die
          Vergleichsseiten desselben Portals. Ausgespielt wird nur, was hier unten als offen steht —
          auch nach dem Schließen und erneuten Öffnen.
        </Alert>

        {fremderLauf && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void laden()}>
                Neu laden
              </Button>
            }
          >
            Für diesen Broker läuft gerade ein Ausspielen (anderer Tab oder Kollege). Speichern und
            Ausspielen sind gesperrt, bis es fertig ist — der Dialog sieht alle 5 Sekunden nach.
          </Alert>
        )}

        {meldung && (
          <Alert severity={meldung.ton} sx={{ mb: 2 }}>
            {meldung.text}
          </Alert>
        )}

        {zustand === null && busy === 'laden' && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={22} />
          </Box>
        )}

        {zustand && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 110 }}>Portal</TableCell>
                  <TableCell>Affiliate-Link</TableCell>
                  <TableCell sx={{ width: 190 }}>Profilseite</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zustand.portale.map((p) => {
                  const eigene = befundeFuer(p.siteKey);
                  const anders =
                    p.profileId && (eingaben[p.siteKey] ?? '').trim() !== (p.affiliateUrl ?? '');
                  const t = p.ausspielZustand ? ROLLOUT_TEXTE[p.ausspielZustand] : null;
                  return (
                    <TableRow key={p.siteKey}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {p.siteKey.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.siteName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {p.profileId ? (
                          <>
                            <TextField
                              size="small"
                              fullWidth
                              value={eingaben[p.siteKey] ?? ''}
                              disabled={laeuft}
                              placeholder="https://…"
                              onChange={(e) => {
                                const v = e.target.value;
                                setEingaben((alt) => ({ ...alt, [p.siteKey]: v }));
                                setBefunde(null);
                                setBestaetigungNoetig(false);
                              }}
                              inputProps={{
                                spellCheck: false,
                                style: { fontFamily: 'monospace', fontSize: 12 },
                              }}
                              sx={
                                anders
                                  ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fff8e1' } }
                                  : undefined
                              }
                            />
                            {anders && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ wordBreak: 'break-all' }}
                              >
                                bisher: {p.affiliateUrl || '—'}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Kein Profil dieses Brokers auf diesem Portal.
                          </Typography>
                        )}
                        {eigene.map((b, i) => (
                          <Typography
                            key={i}
                            variant="caption"
                            display="block"
                            color={BEFUND_FARBE[b.art]}
                          >
                            {BEFUND_PRAEFIX[b.art]}
                            {b.text}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        {p.status !== 'live' ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={p.status ? `Status: ${p.status}` : '—'}
                          />
                        ) : !p.aufPortal ? (
                          <Chip size="small" color="warning" label="live, aber nie ausgespielt" />
                        ) : t ? (
                          <Tooltip title={p.pageUrl ?? ''}>
                            <Chip
                              size="small"
                              label={t.text}
                              color={t.ton === 'default' ? undefined : t.ton}
                              variant={t.ton === 'default' ? 'outlined' : 'filled'}
                            />
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Box mt={3}>
              <Typography variant="subtitle1" fontWeight="bold">
                Offene Seiten ({offene.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Seiten, deren ausgespielter Stand nicht dem gespeicherten entspricht — Profilseiten
                dieses Brokers und Vergleichsseiten, die ihn zeigen (
                {zustand.vergleichsseitenGesamt} live). Seiten mit fremden, noch nicht ausgespielten
                Änderungen sind nicht angehakt.
              </Typography>
              {offene.length === 0 ? (
                <Alert severity="success">Alles ausgespielt — es ist nichts offen.</Alert>
              ) : (
                <Table size="small">
                  <TableBody>{offene.map(zeileOffen)}</TableBody>
                </Table>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={lokalBusy}>
          Schließen
        </Button>
        <Button onClick={pruefen} disabled={laeuft || !zustand || geaendert.length === 0}>
          {busy === 'pruefen' ? 'Prüft…' : 'Prüfen'}
        </Button>
        {bestaetigungNoetig ? (
          <Button
            color="warning"
            variant="contained"
            onClick={() => speichern(true)}
            disabled={laeuft || hatFehler}
          >
            Trotzdem speichern
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => speichern(false)}
            disabled={laeuft || geaendert.length === 0 || hatFehler}
          >
            {busy === 'speichern' ? 'Speichert…' : `Speichern (${geaendert.length})`}
          </Button>
        )}
        <Tooltip title={geaendert.length > 0 ? 'Erst die geänderten Links speichern.' : ''}>
          <span>
            <Button
              variant="contained"
              color="secondary"
              onClick={ausspielen}
              disabled={laeuft || gewaehlteAnzahl === 0 || geaendert.length > 0}
            >
              {busy === 'ausspielen' ? 'Spielt aus…' : `${gewaehlteAnzahl} Seite(n) ausspielen`}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default AffiliateLinksDialog;
